import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { PaymentMethodAdapter, PaymentAdapterContext } from "../types";

const isLiveStripeSession = (publicKey?: string, clientSecret?: string) =>
  Boolean(
    publicKey?.startsWith("pk_") &&
      clientSecret?.includes("_secret_") &&
      !clientSecret.includes("_secret_sandbox")
  );

const StripePaymentForm: React.FC<PaymentAdapterContext> = ({ amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message ?? "Não confirmamos o pagamento. Tente de novo ou escolha outra forma.");
    }

    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {errorMessage ? (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <Button type="submit" disabled={!stripe || isSubmitting} className="w-full bg-success hover:bg-success-subtle">
        {isSubmitting ? "Processando..." : `Pagar R$ ${amount.toFixed(2).replace(".", ",")}`}
      </Button>
    </form>
  );
};

const StripeCardView: React.FC<PaymentAdapterContext> = (context) => {
  const { session, amount, hideTitle = false } = context;
  const stripePromise = useMemo(
    () => (session.publicKey ? loadStripe(session.publicKey) : null),
    [session.publicKey]
  );

  const useLiveStripe = isLiveStripeSession(session.publicKey, session.clientSecret);

  if (useLiveStripe && stripePromise && session.clientSecret) {
    return (
      <Card id="stripe-card-adapter">
        {hideTitle ? null : (
          <CardHeader>
            <h2 className="text-xl font-semibold">Cartão (Stripe)</h2>
          </CardHeader>
        )}
        <CardContent className={hideTitle ? "pt-6" : undefined}>
          <p className="text-text-secondary text-sm mb-4">
            Informe os dados do cartão no formulário seguro abaixo. O valor de{" "}
            <strong>R$ {amount.toFixed(2).replace(".", ",")}</strong> será cobrado após a
            confirmação.
          </p>
          <Elements stripe={stripePromise} options={{ clientSecret: session.clientSecret }}>
            <StripePaymentForm {...context} />
          </Elements>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="stripe-card-adapter-sandbox">
      {hideTitle ? null : (
        <CardHeader>
          <h2 className="text-xl font-semibold">Cartão (Stripe)</h2>
        </CardHeader>
      )}
      <CardContent className={hideTitle ? "space-y-4 pt-6" : "space-y-4"}>
        <p className="text-text-secondary text-sm">
          Pagamento de <strong>R$ {amount.toFixed(2).replace(".", ",")}</strong> via Stripe. Depois
          de confirmar o cartão, aguarde a confirmação nesta página.
        </p>
        <div className="rounded-lg border border bg-surface p-6 text-center text-sm text-text-secondary">
          <p>Formulário de cartão em configuração.</p>
          <p className="mt-2 text-xs text-text-tertiary">
            Quando as chaves de teste estiverem ativas, o formulário seguro aparece aqui
            automaticamente.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export const StripeCardAdapter: PaymentMethodAdapter = {
  provider: "Stripe",
  method: "Card",
  render: (context) => <StripeCardView {...context} />,
};
